import type { Payment } from './usePayments'
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import { StatusPill } from '../../components/StatusPill'
import { fmtPrice } from '../../lib/fmt'
import { activeBcp47 } from '../../lib/locale'
import { FIAT_CURRENCIES } from './usePaymentMutations'
import './payments.css'

interface PaymentRowProps {
  payment: Payment
  /**
   * R6/MP — Zeilen-Aktionen; ohne Handler bleibt die Aktionsspalte leer.
   * onEdit/onDelete werden NUR fuer manuell erfasste Zahlungen gerendert:
   * das Backend beantwortet PATCH/DELETE auf importierte Blockchain-Zahlungen
   * mit 403 imported_readonly (siehe usePaymentMutations.ts).
   */
  onEdit?: (payment: Payment) => void
  onDelete?: (payment: Payment) => void
  /**
   * P1 (v1-Paritaets-Audit 2026-09-01) — v1-Paritaet zu
   * payments_controller#update_contact: reine Contact-Korrektur, AUCH fuer
   * importierte Zahlungen (das Backend nimmt PATCH mit NUR contact_id gezielt
   * von der imported_readonly-Sperre aus).
   */
  onAssignContact?: (payment: Payment) => void
  /**
   * Audit 2026-09-01 (Design-Paritaet Payments): Vorlage zeigt in der Liste
   * den zugeordneten Menschen statt der rohen Wallet-Namen. contact_id ist
   * nur eine ID — der Name kommt aus der (in PaymentsPage einmal geladenen)
   * Contacts-Liste, damit hier nicht pro Zeile nachgefragt wird.
   */
  contactName?: string | null
  /**
   * Audit 2026-09-01 (Design-Paritaet Payments): Vorlage zeigt den Betrag
   * konsequent in Fiat statt roher HNT-Werte. hntPrice kommt aus
   * useCryptoPrices() (PaymentsPage, ein Request fuer alle Zeilen).
   */
  hntPrice?: number | null
  fiatCurrency?: string
}

/**
 * R6/MP: Die Spaltendefinition war doppelt hartkodiert (PaymentsPage + PaymentRow)
 * und ist jetzt eine einzige CSS-Klasse (payments.css `.payment-grid`) statt
 * einer inline gridTemplateColumns-Konstante — nur eine Stylesheet-Regel kann
 * per @media bei 390px auf 3 Spalten kollabieren (Date+Person ausgeblendet,
 * `.payment-col-md`, analog invoices.css `.invoice-col-issued`).
 */
export const PAYMENT_GRID_CLASS = 'list-row payment-grid'
export const PAYMENT_HEAD_CLASS = 'list-head payment-grid'

const METHOD_LABEL_KEYS: Record<string, string> = {
  hnt: 'methods.hnt',
  paypal: 'methods.paypal',
  money_transfer: 'methods.money_transfer',
  cash: 'methods.cash',
}

/**
 * Betrag: Fremdwaehrungsbetrag wenn vorhanden+fiat, sonst HNT umgerechnet
 * zum aktuellen Kurs (hntPrice, aus useCryptoPrices) in der Nutzerwaehrung —
 * die Vorlage zeigt in der Liste konsequent Fiat, nie rohe HNT-Werte. Ohne
 * geladenen Kurs (noch loading) bleibt der HNT-Rohwert als Fallback stehen,
 * statt eine falsche Umrechnung vorzutaeuschen.
 */
function formatAmount(payment: Payment, hntPrice: number | null | undefined, fiatCurrency: string): string {
  if (
    payment.currency_amount != null &&
    payment.currency &&
    (FIAT_CURRENCIES as readonly string[]).includes(payment.currency)
  ) {
    return fmtPrice(payment.currency_amount, payment.currency)
  }
  if (hntPrice != null) {
    // Anders als fmtPrice() (bis zu 4 Nachkommastellen, fuer Wallet-Balances
    // gedacht) zeigt die Vorlage Betraege in der Liste konsequent mit genau
    // 2 Nachkommastellen (z.B. "€61.00") — lokale Formatierung statt
    // fmtPrice() nur fuer diesen umgerechneten Schaetzwert.
    const amount = payment.amount_hnt * hntPrice
    try {
      // Issue #55: Locale leitete sich bisher aus der Währung ab statt aus
      // der aktiven App-Sprache — dieselbe Seite mischte dadurch Formate.
      return new Intl.NumberFormat(activeBcp47(), {
        style: 'currency',
        currency: fiatCurrency.toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount)
    } catch {
      return fmtPrice(amount, fiatCurrency)
    }
  }
  return `${payment.amount_hnt.toFixed(4)} HNT`
}

/**
 * Issue #55 (Nachbesserung): baute bisher ein hartes ISO-Format
 * (`YYYY-MM-DD`) unabhängig von der aktiven App-Sprache zusammen — eine
 * dritte Formatvariante neben den de-DE/en-US-Stellen der restlichen Seite.
 * Folgt jetzt demselben Muster wie `invoiceUtils.formatDate` (activeBcp47()).
 */
function formatDate(iso: string | null): string {
  if (!iso) return '-'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString(activeBcp47(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function PaymentRow({ payment, onEdit, onDelete, onAssignContact, contactName, hntPrice, fiatCurrency = 'eur' }: PaymentRowProps) {
  const { t } = useTranslation('payments')
  const methodKey = payment.payment_method ?? ''
  const methodLabel = METHOD_LABEL_KEYS[methodKey] ? t(METHOD_LABEL_KEYS[methodKey]) : (payment.payment_method ?? '-')
  // Nur manuelle Zahlungen sind aenderbar — importierte laufen sonst in ein 403.
  const isEditable = payment.manual === true
  // Audit 2026-09-01: Vorlage beschreibt die Zeile ("HNT transfer (on-chain)" /
  // Memo bei manuellen Zahlungen) statt roher Sender/Empfaenger-Wallet-Namen.
  const description = payment.manual
    ? (payment.memo || t('row.manualDescriptionFallback'))
    : t('row.importedDescription')

  return (
    <div className={PAYMENT_GRID_CLASS} data-testid={`payment-row-${payment.id}`}>
      <div className="muted payment-col-md" style={{ fontSize: 12 }}>{formatDate(payment.created_at)}</div>
      <div style={{ minWidth: 0 }}>
        {/* P2 (Abnahme 2026-09-01, 390px): fixe Ellipsis-Kuerzung schnitt die
            Beschreibung mitten im Wort ab, sobald Badge+Text nicht in eine
            Zeile passten (schmale Person-Spalte bei 390px). Vorlage
            (payments.html) kuerzt nicht, sondern laesst Text+Badge bei Bedarf
            umbrechen — das uebernehmen wir hier per flexWrap statt nowrap. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, rowGap: 2, flexWrap: 'wrap', fontWeight: 500 }}>
          <span>{description}</span>
          {payment.manual ? (
            <span data-testid="payment-manual-badge" title={t('row.manualBadgeTitle')}>
              <StatusPill variant="accent" label={t('row.manualBadge')} dot={false} />
            </span>
          ) : (
            <StatusPill variant="neutral" label={t('row.importedBadge')} dot={false} title={t('row.importedReadonlyTitle')} />
          )}
        </div>
        <div className="muted payment-amount" style={{ fontSize: 11 }}>
          {methodLabel} · {payment.amount_hnt.toFixed(4)} HNT
        </div>
      </div>
      <div className="payment-col-md" style={{ minWidth: 0 }}>
        {contactName ? (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {contactName}
          </span>
        ) : isEditable ? (
          <span className="muted">—</span>
        ) : (
          onAssignContact && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onAssignContact(payment)}
              data-testid={`payment-assign-contact-${payment.id}`}
              aria-label={t('row.assignContactAriaLabel')}
            >
              {t('row.assignContact')}
            </button>
          )
        )}
      </div>
      <div className="tnum payment-amount" style={{ fontWeight: 600, textAlign: 'right' }}>
        {formatAmount(payment, hntPrice, fiatCurrency)}
      </div>
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        {!isEditable && (
          /* Importierte Blockchain-Zahlung: Betrag/Memo/etc. bleiben
             schreibgeschuetzt (403 imported_readonly) — die Vorlage zeigt
             hier keinerlei Platzhalter, die Contact-Korrektur laeuft ueber
             die Person-Spalte. Leeres Element traegt nur noch Titel/Testid
             fuer a11y/Tests, ohne sichtbaren "—"-Text. */
          <span
            aria-hidden="true"
            title={t('row.importedReadonlyTitle')}
            data-testid={`payment-readonly-${payment.id}`}
          />
        )}
        {isEditable && onEdit && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('row.editAriaLabel')}
            title={t('row.edit')}
            onClick={() => onEdit(payment)}
            data-testid={`payment-edit-${payment.id}`}
          >
            <Pencil size={14} aria-hidden="true" />
          </button>
        )}
        {isEditable && onDelete && (
          <button
            type="button"
            className="btn btn-ghost btn-sm is-danger"
            aria-label={t('row.deleteAriaLabel')}
            title={t('row.delete')}
            onClick={() => onDelete(payment)}
            data-testid={`payment-delete-${payment.id}`}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  )
}
